import { useRef, useEffect, useState } from 'react'
import { useThree, useLoader, useFrame } from '@react-three/fiber'
import { meshBounds, SpotLight, useHelper, useGLTF, useTexture } from '@react-three/drei'
import { SpotLightHelper, Object3D, AudioLoader, AudioListener, Audio } from 'three'
import { useControls } from 'leva'

export default function Television()
{
    const model = useGLTF('./models/electro/television/Television_01_1k.gltf')
    const texture = useTexture('./textures/electro/noise.jpg')

    // traverse model
    useEffect(() => {
        model.scene.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true // Shadow
                child.raycast = meshBounds // Apply meshBounds to each mesh
            }
        })
    }, [model])

    /**
     * Leva
     */
    
    const leva = useControls('tv', 
    {
        // positions
        tvPosition:{x:-1.5,y:-0.1,z:-1.5},
        screenPos:{x:-0.07,y:0.26,z:0.20},
        lightPos:{x:0.06,y:0.24,z:0.08},
        targetPos:{x:1,y:-2,z:1},
        rotation:{value:0.9,min:-2,max:2},

        // sizes
        screenSize:{x:0.4,y:0.3},
        
        // spot light settings
        color:'#008dff',
        angle:{value:0.94,min:0,max:2},
        attenuation:{value:4.02,min:0,max:6},
        anglePower:{value:3.52,min:0,max:6},
        intensity:{value:1,min:0,max:3},
        penumbra:{value:1,min:0,max:2},
        decay:{value:2.54,min:0,max:6}
    },
    { collapsed: true }
    )

    /**
     * Lights
     */

    const lightRef = useRef()
    const screenRef = useRef()
    const ambientLightRef = useRef()

    // Helper
    // useHelper(lightRef, SpotLightHelper, 1)
    
    // Intensity
    useFrame( (state,delta) => {
        lightRef.current.intensity =  (
            15 +
            Math.random() * 5 +
            Math.sin(state.clock.elapsedTime) * 5
            ) * leva.intensity

            const colorBlink = Math.random() * 0.2
            screenRef.current.material.color.g = 0.266 - colorBlink
            screenRef.current.material.color.b = 1 - colorBlink
    } )

    // Target
    const targetRef = useRef(new Object3D())
    const { scene } = useThree()

    // Add target
    useEffect(() => {
        scene.add(targetRef.current)
        lightRef.current.target = targetRef.current
    }, [scene])

    // Position the target
    useEffect(() => {
        targetRef.current.position.set( // Adjust target position as needed
            leva.tvPosition.x + leva.targetPos.x, 
            leva.tvPosition.y + leva.targetPos.y, 
            leva.tvPosition.z + leva.targetPos.z
        )
    }, [ leva.tvPosition,leva.targetPos ])

    /**
     * Sounds
     */

    const { camera } = useThree()
    
    // Load and decode button click sound
    const buttonClickSound = useLoader(AudioLoader, './sounds/analog-appliance-button-15-186961.mp3')
    // Load and decode white noise sound
    const whiteNoiseSound = useLoader(AudioLoader, './sounds/white-noise-6971.mp3')

    const [tvOn, setTvOn] = useState(false); // State to track TV status
    const whiteNoiseRef = useRef(); // Ref to manage white noise sound

    // Initialize and configure white noise sound
    useEffect(() => {
        const listener = new AudioListener();
        camera.add(listener);

        const sound = new Audio(listener);
        sound.setBuffer(whiteNoiseSound);
        sound.setLoop(true); // Loop the white noise
        sound.setVolume(0.1);

        whiteNoiseRef.current = sound;

        return () => {
            camera.remove(listener); // Cleanup
        };
    }, [camera, whiteNoiseSound]);


    // Function to handle playing the button click sound
    const playButtonClickSound = () => {
        // Create a new AudioListener and Audio instance for each play action
        const listener = new AudioListener()
        camera.add(listener) // Attach the listener to the camera

        const sound = new Audio(listener) // Audio is a player
        sound.setBuffer(buttonClickSound) // assign decoded audio to Audio player
        sound.setVolume(0.5)
        sound.play()

        sound.onEnded = () => {
            camera.remove(listener) // Clean up the listener once the sound has played
        }
    }

    /**
     * Clicker
     */
   
    const eventHandler = (event) => 
    {
        lightRef.current.visible = !lightRef.current.visible
        screenRef.current.visible = !screenRef.current.visible
        ambientLightRef.current.visible = !ambientLightRef.current.visible

        event.stopPropagation()

        playButtonClickSound()

        setTvOn(!tvOn); // Toggle TV state
        // Toggle white noise sound based on TV state
        if (!tvOn) {
            whiteNoiseRef.current?.play();
        } else {
            whiteNoiseRef.current?.pause();
        }
    }  

    /**
     * Return
     */

    return <>
    
        <group
            position={[ 
                leva.tvPosition.x, 
                leva.tvPosition.y, 
                leva.tvPosition.z 
            ]}
            rotation-y={leva.rotation}
        >
            {/* Model */}
            <primitive
                object={model.scene}
                raycast={ meshBounds }
                onClick={ eventHandler } 
                onPointerEnter={ () => { document.body.style.cursor = 'pointer' } }
                onPointerLeave={ () => { document.body.style.cursor = 'default' } }
            />

            {/* Screen */}
            <mesh
                ref={screenRef}
                visible={false}
                position={[ 
                    leva.screenPos.x, 
                    leva.screenPos.y, 
                    leva.screenPos.z
                ]}
            >
                <planeGeometry args={[
                    leva.screenSize.x,
                    leva.screenSize.y,
                    leva.screenSize.z
                ]} />
                <meshBasicMaterial 
                    color={leva.color} 
                    map={texture}
                />
            </mesh>
        </group>

        {/* Light */}
        <SpotLight 
            ref={lightRef}
            visible={false}
            castShadow 
            position={[
                leva.tvPosition.x + leva.lightPos.x, 
                leva.tvPosition.y + leva.lightPos.y, 
                leva.tvPosition.z + leva.lightPos.z
            ]} 
            angle={leva.angle}
            attenuation={leva.attenuation}
            anglePower={leva.anglePower}
            intensity={leva.intensity}
            penumbra={leva.penumbra}
            color={leva.color}
            decay={leva.decay}
            shadow-normalBias={ 0.01 }
            shadow-mapSize-width={1024} // Increase shadow map resolution width
            shadow-mapSize-height={1024} // Increase shadow map resolution height
        />
        

        {/* Ambient Light */}
        <ambientLight 
            ref={ ambientLightRef }
            visible={false}
            intensity={ 0.5 * leva.intensity } 
            color={leva.color}
        />
    
    </>
}